// IdentityPage.tsx — shows the authenticated user's email, roles, and scopes.
import { useMemo } from 'react'
import Card from 'react-bootstrap/Card'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'
import NameTag from '../components/NameTag'
import LongLivedTokensCard from '../components/LongLivedTokensCard'
import { useAuth } from '../hooks/useAuth'

const IdentityPage = () => {
    const { email, roles, scopes, photoUrl, loading, error } = useAuth()

    const sortedScopes = useMemo(() => [...(scopes || [])].sort(), [scopes])
    const sortedRoles = useMemo(() => [...(roles || [])].sort(), [roles])

    const scopeGroups = useMemo(() => {
        const g: Record<string, string[]> = {}
        for (const scope of sortedScopes) {
            const parts = scope.split('.')
            const prefix = parts.length >= 2 ? parts[0] : 'other'
            if (!g[prefix]) g[prefix] = []
            g[prefix].push(scope)
        }
        return g
    }, [sortedScopes])

    if (loading) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner animation="border" />
            </div>
        )
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>
    }

    return (
        <>
            <h1 className="mb-4">My Identity</h1>

            <Card className="mb-3">
                <Card.Header>Account</Card.Header>
                <Card.Body className="d-flex align-items-center gap-3">
                    {photoUrl ? (
                        <img
                            src={photoUrl}
                            alt=""
                            referrerPolicy="no-referrer"
                            style={{
                                width: 72,
                                height: 72,
                                borderRadius: '50%',
                                objectFit: 'cover',
                                border: '1px solid var(--bs-border-color)',
                                flexShrink: 0,
                            }}
                        />
                    ) : (
                        <div
                            aria-hidden="true"
                            className="d-flex align-items-center justify-content-center text-muted"
                            style={{
                                width: 72,
                                height: 72,
                                borderRadius: '50%',
                                background: 'var(--bs-secondary-bg)',
                                border: '1px solid var(--bs-border-color)',
                                fontSize: '1.75rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                flexShrink: 0,
                            }}
                        >
                            {email ? email.charAt(0) : '?'}
                        </div>
                    )}
                    <Table size="sm" borderless className="config-table m-0" style={{ width: 'auto' }}>
                        <tbody>
                            <tr>
                                <td className="text-muted text-end pe-3 align-middle" style={{ whiteSpace: 'nowrap' }}>Email</td>
                                <td className="fw-bold"><code>{email}</code></td>
                            </tr>
                            <tr>
                                <td className="text-muted text-end pe-3 align-middle" style={{ whiteSpace: 'nowrap' }}>Roles</td>
                                <td className="fw-bold">{sortedRoles.length}</td>
                            </tr>
                            <tr>
                                <td className="text-muted text-end pe-3 align-middle" style={{ whiteSpace: 'nowrap' }}>Scopes</td>
                                <td className="fw-bold">{sortedScopes.length}</td>
                            </tr>
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <LongLivedTokensCard availableScopes={sortedScopes} />

            <Card className="mb-3">
                <Card.Header>Roles ({sortedRoles.length})</Card.Header>
                <Card.Body>
                    {sortedRoles.length > 0 ? (
                        <div className="d-flex flex-wrap column-gap-3 row-gap-1">
                            {sortedRoles.map((role) => (
                                <NameTag key={role} name={role} kind="role" to={`/roles/${encodeURIComponent(role)}`} />
                            ))}
                        </div>
                    ) : (
                        <span className="text-muted small">No roles assigned</span>
                    )}
                </Card.Body>
            </Card>

            {sortedScopes.length > 0 ? (
                Object.entries(scopeGroups).map(([prefix, groupScopes]) => (
                    <Card key={prefix} className="mb-3">
                        <Card.Header><code>{prefix}.*</code></Card.Header>
                        <Card.Body>
                            <div className="d-flex flex-wrap column-gap-3 row-gap-1">
                                {groupScopes.map((scope) => (
                                    <NameTag key={scope} name={scope} kind="scope" to={`/scopes/${encodeURIComponent(scope)}`} />
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                ))
            ) : (
                <Card className="mb-3">
                    <Card.Header>Scopes</Card.Header>
                    <Card.Body>
                        <span className="text-muted small">No scopes granted</span>
                    </Card.Body>
                </Card>
            )}
        </>
    )
}

export default IdentityPage
